import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Score, { getScoreValue, SCORE_TYPE } from '../atoms/Score';
import Avatar from '../atoms/Avatar';
import Stars from '../atoms/Stars';
import MultiSportList from './MultiSportList';
import { getPlayerPath } from '../services/path';
import { getPrimaryCollege, getPrimaryHighSchool, getPrimaryNFLTeam, getPrimaryPosition, PlayerType } from '../services/player';
import { State } from '../redux/reducers';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import {
  COLOR_BACKGROUND_LIGHT,
  COLOR_BLUE,
  COLOR_DARK_BLUE,
  COLOR_DARK_GRAY,
  COLOR_MIDDLE_GRAY,
  COLOR_TEXT,
  COLOR_WHITE,
} from '../styles/colors';
import ComparePlayer, { ComparePlayerType } from '../types/ComparePlayer';
import { TEAM_TYPE } from '../types/Team';
import LabledScore from '../atoms/LabledScore';

interface TopPlayerCardProps {
  className?: string;
  index: number;
  player: ComparePlayer;
  comparePlayerType: string | undefined;
  newProfileEnabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  topPlayerCard: {
    width: 'auto',
    minWidth: 240,
    borderRadius: 0,
  },

  header: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 52,
    padding: theme.spacing(1, 2),
    background: COLOR_BACKGROUND_LIGHT,
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(13),
    color: COLOR_TEXT,
  },
  rank: {
    lineHeight: 1,
    textAlign: 'center',
  },
  avatar: {
    position: 'absolute',
    zIndex: 1,
    top: 8,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 78,
    height: 78,
  },
  playerLink: {
    color: COLOR_BLUE,
    fontSize: theme.typography.pxToRem(16),

    '&:hover': {
      color: COLOR_DARK_BLUE,
    }
  },
  percentile: {},

  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    background: COLOR_WHITE,
  },

  playerInfo: {
    width: '100%',
    marginTop: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontWeight: 700,
  },
  textInfo: {
    marginTop: theme.spacing(.5),
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    color: COLOR_TEXT,
  },

  playerScoreWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'space-between',

    '&:first-of-type': {
      marginLeft: 0,
    },

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row',
      gap: theme.spacing(1),
    },
  },

  checkIconClass: {
    height: 26,
    width: 26,

    [theme.breakpoints.down('sm')]: {
      height: 22,
      width: 22,
    },
  },

  playerStats: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: 200,
    margin: `${theme.spacing(2)}px auto 0 auto`,
  },
  centeredScores: {
    '& $playerStats': {
      justifyContent: 'center',
    },
  },
  playerScore: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(0, 1),
    fontSize: theme.typography.pxToRem(16),
  },
  scoreSize: {
    width: 52,
    height: 52,
    margin: 0,
  },

  playerScoreNumber: {
    marginLeft: theme.spacing(1),
  },
  
  noScoreText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 700,
  },

  stars: {
    height: 32,
    alignItems: 'center',
  },

  multiSport: {
    gap: theme.spacing(1),
    alignItems: 'center',
    height: 32,
  },
  multiSportIcon: {
    width: 26,
    height: 26,
    opacity: '0.5',
  },

  playerMultiSColumn: {
      display: 'flex',
  
      [theme.breakpoints.down('sm')]: {
        flexGrow: 1,
        flexWrap: 'wrap',
        gap: theme.spacing(2, 0),
        justifyContent: 'center',
        margin: `0 ${theme.spacing(-2)}px`,
        padding: theme.spacing(3, 2),
        backgroundColor: COLOR_BACKGROUND_LIGHT,
      },
    },
    playerMultiSSection: {
      flex: '0 0 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(.5),
      minHeight: 60,
      minWidth: 96,
      padding: theme.spacing(0, 4),
      borderRight: `2px solid ${COLOR_MIDDLE_GRAY}`,
  
      '&:first-child': {
        paddingLeft: 0,
      },
  
      '&:last-child': {
        paddingRight: 0,
        borderRight: 0,
      },
  
      '@media (max-width: 1680px)': {
        padding: theme.spacing(0, 1.5),
      },
    },
    multiSportSpecialistChip: {
      backgroundColor: COLOR_DARK_GRAY,
      borderRadius: '999px',
      color: COLOR_WHITE,
      fontFamily: FONT_PROXIMA_NOVA,
      fontSize: theme.typography.pxToRem(12),
      fontWeight: 700,
      lineHeight: 1,
      padding: theme.spacing(1, 2.5),
      marginBottom: theme.spacing(1),
    },
    multiSportCount: {
      fontSize: theme.typography.pxToRem(22),
      fontWeight: 700,
      color: COLOR_TEXT,
      whiteSpace: 'nowrap',
      lineHeight: 1.1,
    },
    evalSportCount: {
      fontSize: theme.typography.pxToRem(15),
      fontWeight: 700,
      color: COLOR_TEXT,
      whiteSpace: 'nowrap',
      lineHeight: 1.1,
      paddingRight: theme.spacing(1),
    },
    evalColorSwatch: {
      width: 55,
      height: 24,
      borderRadius: 5,
      boxShadow: '0 0 0 5px #dbdbdb',
      boxSizing: 'content-box' as const,
    },
    multiSportLabel: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      minHeight: 26,
      textAlign: 'center',
      whiteSpace: 'nowrap',
      fontSize: theme.typography.pxToRem(15),
      fontWeight: 700,
      textTransform: 'uppercase',
    },
    // multiSportIcon: {
    //   display: 'flex',
    //   alignItems: 'center',
    //   justifyContent: 'center',
    //   width: 44,
    //   minWidth: 44,
    //   height: 26,
  
    //   '& svg': {
    //     width: '100%',
    //     height: '100%',
    //   },
    // },
    multiSportTooltipList: {
      gap: theme.spacing(1),
      alignItems: 'center',
    },
    multiSportTooltipIcon: {
      margin: 0,
      color: COLOR_WHITE,
    },
}), { name: TopPlayerCard.name });

export default function TopPlayerCard (props:TopPlayerCardProps) {
  const {
    className,
    index,
    player,
    comparePlayerType,
    newProfileEnabled = false,
  } = props;
  const classes = useStyles();
  // const newProfileEnabled = useSelector((state:State) => Boolean(state.user?.program?.newProfileEnabled));

  const primaryHS = getPrimaryHighSchool(player);
  const primaryCollege = getPrimaryCollege(player);
  const primaryNFLTeam = getPrimaryNFLTeam(player);

  let city:string = primaryHS?.city || '';
  let state:string = primaryHS?.state || '';
  let classYear = primaryHS?.graduatingClass || '';
  let position = getPrimaryPosition(player, PlayerType.HIGH_SCHOOL) || '';
  let teamName = '';
  const playerMultiSport = player?.multiSport?.length ?? 0

  if (comparePlayerType === TEAM_TYPE.NFL) {
    teamName = primaryNFLTeam?.team?.name || '';
    classYear = primaryNFLTeam?.draftYear || '';
    position = getPrimaryPosition(player, PlayerType.NFL) || '';
  } else if (comparePlayerType === TEAM_TYPE.COLLEGE) {
    teamName = primaryCollege?.team?.name || '';
    classYear = primaryCollege?.recruitingClass || '';
    position = getPrimaryPosition(player, PlayerType.COLLEGE) || '';
  }

  const address:string = city && state ? `${city}, ${state}` : (city || state);
  const centeredScores = !player.pai || !player.combine;
  const noPAIScore = !player?.pai?.length || typeof getScoreValue(player.pai) !== 'number';
  const noCombineScore = !player?.hsCombines?.length;
  const noScores = noPAIScore && noCombineScore;
  const displayMultiSport = noScores && player?.multiSport?.length > 0;

  function getScoresBasicPlayerType (comparePlayerType:string | undefined) {
    switch (comparePlayerType) {
      case TEAM_TYPE.HIGH_SCHOOL:
        return (
          <>
          <div className={classes.playerScoreWrapper}>
            <LabledScore
              className={clsx(classes.playerScore, classes.scoreSize)}
              noScoreTextClass={classes.noScoreText}
              type={SCORE_TYPE.PAI}
              scoreList={player.pai}
              // label='PAI'
            />
          </div>
          <div className={classes.playerScoreWrapper}>
            <LabledScore
              className={clsx(classes.playerScore, classes.scoreSize)}
              noScoreTextClass={classes.noScoreText}
              checkIconClass={classes.checkIconClass}
              type={SCORE_TYPE.COMBINE}
              scoreList={player.combine}
              combines={player.hsCombines}
              // label='COM'
            />
          </div>
          <div className={clsx(classes.playerScoreWrapper)}>
            {
              player?.bestHighSchoolMPH?.mph ?
                <LabledScore
                  className={clsx(classes.playerScore, classes.scoreSize)}
                  noScoreTextClass={classes.noScoreText}
                  checkIconClass={classes.checkIconClass}
                  type={SCORE_TYPE.MPH}
                  value={(player?.bestHighSchoolMPH?.mph) || undefined}
                  totalValue={(player?.bestHighSchoolMPH?.mph) || undefined}
                  isHSMPH={true}
                  // label='MPH'
                />
              :
              <>
              {/* {
              playerMultiSport > 0 &&  */}
              <LabledScore
                className={clsx(classes.playerScore, classes.scoreSize)}
                noScoreTextClass={classes.noScoreText}
                checkIconClass={classes.checkIconClass}
                type={SCORE_TYPE.MULTISPORTS}
                value={playerMultiSport === 0 ? 0.001 : playerMultiSport}
                totalValue={Math.max(playerMultiSport, 1)}
                multiSportTooltip={
                  playerMultiSport > 0 ? (
                    <MultiSportList
                      className={classes.multiSport}
                      iconClassName={clsx(classes.multiSportTooltipIcon)}
                      list={player?.multiSport}
                    />
                  ) : (
                    <div>Specialist</div>
                  )
                }
              />
              {/* } */}
              </>
            }
          </div>
          </>
              )
      case TEAM_TYPE.COLLEGE:
        return (
          <>
          <div className={classes.playerScoreWrapper}>
            <LabledScore
              className={clsx(classes.playerScore, classes.scoreSize)}
              noScoreTextClass={classes.noScoreText}
              type={SCORE_TYPE.PAI}
              scoreList={player.pai}
              // label='PAI'
            />
          </div>
          <div className={classes.playerScoreWrapper}>
            <LabledScore
              className={clsx(classes.playerScore, classes.scoreSize)}
              noScoreTextClass={classes.noScoreText}
              checkIconClass={classes.checkIconClass}
              type={SCORE_TYPE.PPI}
              value={player.ppi || undefined}
              // label='PPI'
            />
          </div>
          <div className={clsx(classes.playerScoreWrapper)}>
            <LabledScore
              className={clsx(classes.playerScore, classes.scoreSize)}
              noScoreTextClass={classes.noScoreText}
              checkIconClass={classes.checkIconClass}
              type={SCORE_TYPE.MPH}
              value={(player?.bestCollegeMPH?.mph) || undefined}
              totalValue={(player?.bestCollegeMPH?.mph) || undefined}
              isHSMPH={false}
              // label='MPH'
            />
            {/* {
              player?.bestCollegeMPH?.mph ?
                <LabledScore
                  className={clsx(classes.playerScore, classes.scoreSize)}
                  noScoreTextClass={classes.noScoreText}
                  checkIconClass={classes.checkIconClass}
                  type={SCORE_TYPE.MPH}
                  value={(player?.bestCollegeMPH?.mph) || undefined}
                  totalValue={(player?.bestCollegeMPH?.mph) || undefined}
                  // label='MPH'
                />
              :
              <>
              {
              playerMultiSport > 0 && 
              <LabledScore
                className={clsx(classes.playerScore, classes.scoreSize)}
                noScoreTextClass={classes.noScoreText}
                checkIconClass={classes.checkIconClass}
                type={SCORE_TYPE.MULTISPORTS}
                value={playerMultiSport === 0 ? 0.001 : playerMultiSport}
                totalValue={Math.max(playerMultiSport, 1)}
                multiSportTooltip={
                  playerMultiSport > 0 ? (
                    <MultiSportList
                      className={classes.multiSport}
                      iconClassName={clsx(classes.multiSportTooltipIcon)}
                      list={player?.multiSport}
                    />
                  ) : (
                    <div>Specialist</div>
                  )
                }
              />
              }
              </>
            } */}
          </div>
          </>
              )
      case TEAM_TYPE.NFL:
        return (
          <>
          <div className={classes.playerScoreWrapper}>
            <LabledScore
              className={clsx(classes.playerScore, classes.scoreSize)}
              noScoreTextClass={classes.noScoreText}
              type={SCORE_TYPE.PAI}
              scoreList={player.pai}
              // label='PAI'
            />
          </div>
          <div className={classes.playerScoreWrapper}>
            <LabledScore
              className={clsx(classes.playerScore, classes.scoreSize)}
              noScoreTextClass={classes.noScoreText}
              checkIconClass={classes.checkIconClass}
              type={SCORE_TYPE.PPI}
              value={player.ppi || undefined}
              // label='PPI'
            />
          </div>
          <div className={clsx(classes.playerScoreWrapper)}>
            <LabledScore
              className={clsx(classes.playerScore, classes.scoreSize)}
              noScoreTextClass={classes.noScoreText}
              checkIconClass={classes.checkIconClass}
              type={SCORE_TYPE.PRO_COMBINE}
              value={player.nflCombineScore || undefined}
              combines={player.nflCombines}
              // label='Pro'
            />
          </div>
          </>
              )
      default:
        return '';
    }
  }

  return (
    <Card className={clsx(
      classes.topPlayerCard,
      centeredScores && classes.centeredScores,
      className,
    )}>
      <div className={classes.header}>
        <div className={classes.rank}>RANK<br/><b>#{index + 1}</b></div>

        <Avatar
          className={classes.avatar}
          src={player.photoUrl}
          alt={`Photo of ${player.firstName} ${player.lastName}`}
        />

        <div className={classes.percentile}>
          <b>{Math.round(player.percentMatch * 100)}%</b>
        </div>
      </div>

      <CardContent className={classes.content}>
        <div className={classes.playerInfo}>
          <Link
            className={classes.playerLink}
            to={`${getPlayerPath(player.slug, newProfileEnabled)}?`}
            target='_blank'
          >
            {player.firstName} {player.lastName}
          </Link>

          <div className={classes.textInfo}>
            {position}
            {(position && classYear) ? ` | `: ''}
            {classYear}
            {comparePlayerType === TEAM_TYPE.HIGH_SCHOOL && (
              <>
                {((position || classYear) && address) ? ` | `: ''}
                {address}
              </>
            )}
            {comparePlayerType !== TEAM_TYPE.HIGH_SCHOOL && (
              <>
                {((position || classYear) && !!teamName) ? ` | `: ''}
                {teamName}
              </>
            )}
          </div>
        </div>
        {
          newProfileEnabled ?
            <div className={classes.playerStats}>
              {getScoresBasicPlayerType(comparePlayerType)}
            </div>
          :
            <div className={classes.playerStats}>
              {!noPAIScore && (
                <div className={classes.playerScore}>
                  PAI:
                  <Score
                    className={classes.playerScoreNumber}
                    type={SCORE_TYPE.PAI}
                    scoreList={player.pai}
                  />
                </div>
              )}

              {!noCombineScore && (
                <div className={classes.playerScore}>
                  Combine:
                  <Score
                    className={classes.playerScoreNumber}
                    type={SCORE_TYPE.COMBINE}
                    scoreList={player.combine}
                    combines={player.hsCombines}
                  />
                </div>
              )}

              {(noScores && (typeof player?._247Star === 'number' || typeof player?.compStar === 'number')) && (
                <Stars
                  className={classes.stars}
                  value={player?._247Star || player?.compStar || 0}
                />
              )}

              {displayMultiSport && (
                <MultiSportList
                  className={classes.multiSport}
                  iconClassName={classes.multiSportIcon}
                  list={player.multiSport}
                />
              )}
            </div>
        }

      </CardContent>
    </Card>
  );
}
